<template>
    <v-dialog
        v-model="showDialog"
        persistent
        width="500"
        scrollable
        :fullscreen="this.$vuetify.breakpoint.xsOnly"
    >
        <v-card v-if="!showCameraOption">
            <v-card-title>
                Upload
            </v-card-title>
            <v-divider />
            <v-card-text 
                ref="cardText"
                class="pt-3"
            >
                <v-form
                    ref="form"
                >
                    <div v-if="showAlert">
                        <v-alert
                            v-model="showAlert"
                            text
                            color="#122448"
                            type="error"
                            dismissible
                        >
                            {{ alertMessage }}
                        </v-alert>
                    </div>
                    <div
                        v-if="!isUploading"
                        class="imageUploadSection"
                        @dragover="dragover"
                        @drop="drop"
                    >
                        <UploadIcon />
                        <v-card-text style="text-align: center">
                            <strong>Drag and drop files here</strong> or
                        </v-card-text>
                        <v-btn
                            style="text-transform: none !important"
                            class="text-subtitle-2"
                            outlined
                            :loading="isSelecting" 
                            @click="handleFileUploadButton"
                        >
                            Choose Files
                        </v-btn>
                        <input 
                            ref="camerainput" 
                            type="file" 
                            accept="image/*" 
                            capture="environment" 
                            style="display:none;" 
                            @change="onFileChanged('camera')" 
                        >
                        <input 
                            ref="file"
                            multiple 
                            class="d-none" 
                            type="file" 
                            accept=".pdf,.jpg,.jpeg,.png,.tiff,.tif,.txt,.heic"
                            @change="onFileChanged"
                        >
                        <v-card-text class="fileUploadInfoText">
                            Supports: JPG, PNG, HEIC, PDF, TIFF, TXT<br>
                            Max file size: 8.3 MB
                        </v-card-text>
                    </div>
                    <div
                        v-if="isUploading"
                        class="progressBarSection"
                    >
                        <v-progress-circular
                            :rotate="-90"
                            :value="uploadProgress"
                            color="#122448"
                            :size="60"
                        />
                        <v-card-text class="imageProgressText">
                            Uploading {{ selectedFiles.length }} Image{{ selectedFiles.length>1 ? 's' : '' }}
                        </v-card-text>
                    </div>
                    <v-row
                        v-if="selectedFiles.length > 0"
                        class="m-0 mb-3"
                    >
                        <v-col>
                            <v-simple-table class="image-table">
                                <template v-slot:default>
                                    <thead>
                                        <tr>
                                            <th class="text-left">
                                                File Name
                                            </th>
                                            <th class="text-left">
                                                Size
                                            </th>
                                            <th class="text-left">
                                                Type
                                            </th>
                                            <th class="text-left">
                                                Delete
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr
                                            v-for="(item, index) in selectedFilesData"
                                            :key="index"
                                        >
                                            <td>{{ item.name }}</td>
                                            <td>{{ bytesToSize(item.size) }}</td>
                                            <td>{{ getImageType(item) }}</td>
                                            <td>
                                                <v-icon
                                                    class="mr-2"
                                                    @click="removeSelectedImage(index)"
                                                >
                                                    mdi-delete-outline
                                                </v-icon>
                                            </td>
                                        </tr>
                                    </tbody>
                                </template>
                            </v-simple-table>
                        </v-col>
                    </v-row>
                    <v-text-field
                        v-model="caption"
                        label="Caption"
                        outlined
                    />
                    <v-textarea
                        v-model="comment"
                        outlined
                        label="Comment"
                    />
                    <v-select
                        v-model="memberCode"
                        :items="memberCodeList"
                        item-text="member_code"
                        item-value="member_code"
                        outlined
                        label="Code"
                    />
                </v-form>
            </v-card-text>
            <v-divider />
            <v-card-actions class="v-card-action">
                <v-spacer />
                <v-btn
                    style="text-transform: none !important"
                    class="text-subtitle-2"
                    outlined
                    :width="this.$vuetify.breakpoint.xs ? '140px' : '76px'"
                    @click="cancel"
                >
                    Cancel
                </v-btn>
                <v-btn
                    style="text-transform: none !important"
                    class="text-subtitle-2"
                    :disabled="!formValid"
                    depressed
                    :loading="isUploading"
                    color="primary"
                    :width="this.$vuetify.breakpoint.xs ? '140px' : '76px'"
                    @click="uploadImage"
                >
                    Upload
                </v-btn>
                <v-spacer v-if="this.$vuetify.breakpoint.xs" />
            </v-card-actions>
        </v-card>
        <v-bottom-sheet
            v-if="sheet"
            v-model="sheet"
        >
            <v-list>
                <v-subheader class="text-h6">
                    Add Image
                </v-subheader>
                <v-divider />
                <v-list-item
                    @click="handleOpenCamera"
                >
                    <v-list-item-icon class="mr-4">
                        <v-icon>mdi-camera</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Open Camera</v-list-item-title>
                </v-list-item>
                <v-list-item
                    @click="handleFileImport"
                >
                    <v-list-item-icon class="mr-4">
                        <v-icon>mdi-image</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Choose From Files</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-bottom-sheet>
        <v-card v-if="showCameraOption">
            <div
                v-if="showCameraOption"
                class="web-camera-box"
                align="center"
                justify="center"
            >
                <div
                    v-show="isCameraOpen && isLoading"
                    class="camera-loading"
                >
                    Opening camera
                </div>
                
                <div
                    v-if="isCameraOpen"
                    v-show="!isLoading"
                    align="center"
                    justify="center"
                    class="camera-box"
                    :class="{ 'flash' : isShotPhoto }"
                >
                    <div
                        class="camera-shutter"
                        :class="{'flash' : isShotPhoto}"
                    />
                    <video
                        v-show="!isPhotoTaken"
                        ref="camera"
                        autoplay
                        style="width:100%"
                    /> 
                    <canvas
                        v-show="isPhotoTaken"
                        id="photoTaken"
                        ref="canvas"
                        style="width:100%"
                        :width="640"
                        :height="480"
                    />
                </div>
                
                <v-card-actions
                    v-if="isCameraOpen && !isLoading"
                    class="v-card-action camera-action"
                >
                    <v-spacer />
                    <v-btn
                        style="text-transform: none !important"
                        class="text-subtitle-2"
                        outlined
                        @click="handleCloseCamera"
                    >
                        Cancel
                    </v-btn>
                    <v-btn
                        style="text-transform: none !important"
                        class="text-subtitle-2"
                        depressed
                        color="primary"
                        width="110px"
                        @click="takePhoto"
                    >
                        {{ !isPhotoTaken ? 'Capture' : "Recapture" }}
                    </v-btn>
                    <v-btn
                        style="text-transform: none !important"
                        class="text-subtitle-2"
                        depressed
                        :disabled="!isPhotoTaken"
                        color="primary"
                        @click="saveCapturedImage"
                    >
                        Save
                    </v-btn>
                    <v-spacer v-if="this.$vuetify.breakpoint.xs" />
                </v-card-actions>
            </div>
        </v-card>
    </v-dialog>
</template>

<script>
import webtmsBackend from '../services/webtmsBackend';
import UploadIcon from './CustomIcons/UploadIcon.vue';

export default {
    components: {
        UploadIcon,
    },
    props: {
        show: {
            type: Boolean,
            default: false,
        }
    },
    data: () => ({
        caption: '',
        comment: '',
        formValid: false,
        memberCode: '',
        isSelecting: false,
        selectedFiles: [],
        selectedFilesData: [],
        headers: [
            {
                text: 'File Name',
                value: 'name',
                align: 'left',
                width: '170px',
                sortable: false,
            },
            {
                text: 'Size',
                value: 'size',
                align: 'left',
                width: '70px',
                sortable: false,
            },
            {
                text: 'Type',
                value: 'type',
                align: 'left',
                width: '30px',
                sortable: false,
            },
            {
                text: 'Delete',
                value: 'delete',
                align: 'center',
                width: '30px',
                sortable: false,
            }
        ],
        isUploading: false,
        uploadProgress: 0,
        showAlert: false,
        alertMessage: '',
        sheet: false,
        isCameraOpen: false,
        isPhotoTaken: false,
        isShotPhoto: false,
        isLoading: false,
        link: '#',
        backCamera: true,
        showCameraOption: false,
        camVidHeight: 0,
        camVidWidth: 0
    }),    
    computed: {
        showDialog: {
            get() {
                return this.show;                
            },
            set() {
                this.$emit('closeImageUploadDialog', this.isUploading);
                this.isUploading = false;
            },
        },
        selectedMemberCodeRule() {
            return [
                this.memberCode.length > 0 || 'Please select a member code.'
            ];
        },
        memberCodeList() {
            return this.$store.getters.ticketDetails.Ticket_Responses;
        },
        getTicketDetails() {
            return this.$store.getters.ticketDetails;
        },
        getUser() {
            return this.$store.getters.user;
        }
    },
    watch: {
        showDialog(show) {
            if(!show) {
                this.clearAllFields();
                this.$refs.form.resetValidation();
            }
        },
        caption() {
            this.validateForm();
        },
        comment() {
            this.validateForm();
        },
        memberCode() {
            this.validateForm();
        },
        selectedFiles() {
            this.validateForm();
            const data = [];
            for (const fileObject of this.selectedFiles) {
                data.push({
                    'lastModified': fileObject.lastModified,
                    'lastModifiedDate': fileObject.lastModifiedDate,
                    'name': fileObject.name,
                    'size': fileObject.size,
                    'type': fileObject.type
                });
            }
            this.selectedFilesData = [...data];
        }
    },
    created() {
        if(this.memberCodeList.length === 1) {
            this.memberCode = this.memberCodeList[0].member_code;
        }
    },
    methods: {
        cancel() {
            this.showDialog=false;
        },
        async uploadImage() {
            this.$vuetify.goTo(this.$refs.cardText, {'container': this.$refs.cardText});
            this.isUploading = true;
            let fileCount = 1;
            let totalFileLength = 0;
            let totalCompleted = 0;
            for(const selectedFile of this.selectedFiles) {
                totalFileLength += selectedFile.size;
            }

            for(const selectedFile of this.selectedFiles) {
                let fileCaption = '';
                if (this.selectedFiles.length > 1) {
                    fileCaption = `${this.caption} (${fileCount++}/${this.selectedFiles.length})`;
                } else {
                    fileCaption = this.caption;
                }
                
                const formData = new FormData();
                formData.append('tid', this.getTicketDetails.Tickets.tid);
                formData.append('account', this.getUser.account);
                formData.append('ticket', this.getTicketDetails.Tickets.ticket);
                formData.append('revision', this.getTicketDetails.Tickets.revision);
                formData.append('caption', fileCaption);
                formData.append('comment', this.comment);
                formData.append('code', this.memberCode);
                formData.append('visible', 'public');
                formData.append('file', selectedFile);

                const config = {
                    onUploadProgress: (progressEvent) => {
                        const { loaded } = progressEvent;
                        totalCompleted += loaded;
                        const percent = Math.floor((totalCompleted * 100) / totalFileLength);
                        if(percent <= 100) {
                            this.uploadProgress = percent;
                        }
                    }
                };
                
                await webtmsBackend.uploadImage(formData, config);
            }
            this.showDialog = false;
        },
        handleFileImport() {
            this.isSelecting = true;
            window.addEventListener('focus', () => {
                this.isSelecting = false;
            }, { once: true });
            this.$refs.file.click();
            this.sheet = false;
        },
        onFileChanged(type) {
            let files = [];
            if (type === 'camera') {
                files = [...this.$refs.camerainput.files];
            } else {
                files = [...this.$refs.file.files];
            }
            const filteredFiles = [];
            let maxSizeFilesCount = 0;
            let invalidFilesCount = 0;
            files.forEach((file) => {
                console.log(file);
                const fileSize = file.size / 1024 / 1024;
                if (!['image/jpeg', 'image/png', 'application/pdf', 'image/tiff', 'text/plain', 'image/heic'].includes(file.type) && !file.name.toLowerCase().endsWith('.heic')) {
                    invalidFilesCount++;
                } else if (fileSize > 8.3) {
                    maxSizeFilesCount++;
                } else {
                    filteredFiles.push(file);
                }
            });

            this.selectedFiles = [...filteredFiles, ...this.selectedFiles];

            if (maxSizeFilesCount > 0 || invalidFilesCount > 0) {
                this.alertMessage = `${maxSizeFilesCount+invalidFilesCount} file(s) excluded. Supports: JPG, PNG, HEIC, PDF, TIFF, TXT. Max file size: 8.3 MB.`;
                this.showAlert = true;
            } else {
                this.alertMessage = '';
                this.showAlert = false;
            }
            this.sheet = false;
        },
        getImageType(file) {
            let mimeType = file.type;
            // on windows, at least, the mime type is not set for heic files
            if (file.name.toLowerCase().endsWith('.heic')) {
                mimeType = 'image/heic';
            }
            if (mimeType === 'image/jpeg') {
                return 'JPG';
            } else if (mimeType === 'image/png') {
                return 'PNG';
            } else if (mimeType === 'application/pdf') {
                return 'PDF';
            } else if (mimeType === 'image/tiff') {
                return 'TIFF';
            } else if (mimeType === 'text/plain') {
                return 'TXT';
            } else if (mimeType === 'image/heic') {
                return 'HEIC';
            }
            return '';
        },
        bytesToSize(bytes) {
            const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
            if (bytes === 0) return 'n/a';
            const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)), 10);
            if (i === 0) return `${bytes} ${sizes[i]}`;
            return `${(bytes / (1024 ** i)).toFixed(1)} ${sizes[i]}`;
        },
        removeSelectedImage(index) {
            let currentSelectedFiles = [...this.selectedFiles];
            currentSelectedFiles.splice(index, 1);
            this.selectedFiles = [...currentSelectedFiles];
        },
        dragover(event) {
            event.preventDefault();
        },
        drop(event) {
            event.preventDefault();
            this.$refs.file.files = event.dataTransfer.files;
            this.onFileChanged();
            event.currentTarget.classList.add('bg-gray-100');
            event.currentTarget.classList.remove('bg-green-300');
        },
        clearAllFields() {
            this.uploadProgress = 0;
            this.selectedFiles = [];
            this.caption = '';
            this.comment = '';
            this.memberCode = '';
            this.showAlert = '';
        },
        validateForm() {
            this.formValid = this.selectedFiles.length>0 && this.memberCode.trim() !== '';
        },
        handleFileUploadButton() {
            if(this.$vuetify.breakpoint.xs) {
                this.sheet = true;
            } else {
                this.handleFileImport();
            }
        },
        toggleCamera() {
            if (this.isCameraOpen) {
                this.isCameraOpen = false;
                this.isPhotoTaken = false;
                this.isShotPhoto = false;
                this.stopCameraStream();
            } else {
                this.isCameraOpen = true;
                this.createCameraElement();
            }
        },
        createCameraElement() {
            this.isLoading = true;
            const constraints = (window.constraints = {
                audio: false,
                video: {
                    height: 480,
                    width: 640,
                    facingMode: this.backCamera ? 'environment' : 'user'
                }
            });
            navigator.mediaDevices
                .getUserMedia(constraints)
                .then(stream => {
                    this.isLoading = false;
                    this.$refs.camera.srcObject = stream;
                })
                .catch(error => {
                    this.isLoading = false;
                    console.log(`May the browser didn't support or there is some errors: `, error);
                });
        },
        stopCameraStream() {
            let tracks = this.$refs.camera.srcObject.getTracks();
            tracks.forEach(track => {
                track.stop();
            });
        },
        takePhoto() {
            const camera = this.$refs.camera;
            if(!this.isPhotoTaken) {
                this.isShotPhoto = true;
                const FLASH_TIMEOUT = 50;
                setTimeout(() => {
                    this.isShotPhoto = false;
                }, FLASH_TIMEOUT);
            }
            this.isPhotoTaken = !this.isPhotoTaken;
            const context = this.$refs.canvas.getContext('2d');
            context.drawImage(camera, 0, 0, 640, 480);
        }, 
        downloadImage() {
            const download = document.getElementById('downloadPhoto');
            const canvas = document.getElementById('photoTaken').toDataURL('image/jpeg')
                .replace('image/jpeg', 'image/octet-stream');
            download.setAttribute('href', canvas);
        },
        handleOpenCamera() {
            this.sheet = false;
            this.$refs.camerainput.click();
        },
        handleCloseCamera() {
            this.isCameraOpen = false;
            this.isPhotoTaken = false;
            this.isShotPhoto = false;
            this.stopCameraStream();
            this.showCameraOption = false;
        },
        saveCapturedImage() {
            const dataUrl = document.getElementById('photoTaken').toDataURL('image/jpeg');
            var arr = dataUrl.split(','), mime = arr[0].match(/:(.*?);/)[1],
                bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n);
            while(n--){
                u8arr[n] = bstr.charCodeAt(n);
            }
            const imageFile = new File([u8arr], 'camera_'+Date.now()+'.jpg', {type:mime});
            this.selectedFiles = [imageFile, ...this.selectedFiles];
            this.handleCloseCamera();
        }
    }
};
</script>
<style>
    .imageUploadSection {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        position: static;
        height: 240px;
        left: 0px;
        top: 0px;
        background: var(--ll-surface-subtle);
        border: 1px dashed var(--ll-border);
        box-sizing: border-box;
        border-radius: 4px;
        flex: none;
        order: 0;
        align-self: stretch;
        flex-grow: 0;
        margin: 16px 0px 30px 0;
    }
    .fileUploadInfoText {
        text-align: center;
        font-size: 10px;
        line-height: 15px;
        color: var(--ll-text-secondary);
        padding-bottom: 0;
    }
    .progressBarSection {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        padding: 24px 16px;
        position: static;
        left: 0px;
        top: 0px;
        background: var(--ll-surface-subtle);
        border: 1px dashed var(--ll-border);
        box-sizing: border-box;
        border-radius: 4px;
        flex: none;
        order: 0;
        align-self: stretch;
        flex-grow: 0;
        margin: 16px 0px 30px 0;
    }
    .imageProgressText {
        font-weight: 500;
        font-size: 14px;
        line-height: 20px;
        align-items: center;
        text-align: center;
        letter-spacing: 0.0025em;
        color: var(--ll-text-primary);
        flex: none;
        order: 0;
        flex-grow: 0;
        padding-bottom: 0;
    }
    .v-card-action {
        padding: 8px 24px !important;
    }
    .camera-action {
        bottom: 0;
        position: fixed;
        width: 100%;
    }
    .image-table td:first-child {
        word-wrap: anywhere;
    }
</style>
